import { isNumber } from 'lodash';
import { lazy, Suspense } from 'react';

import LoginCard from 'hb-builder/components/common/LoginCard';
import EmptyDynamicState from 'hb-builder/components/common/state/EmptyDynamicState';
import ErrorState from 'hb-builder/components/common/state/ErrorState';
import SuspenseFallback from 'hb-builder/components/common/SuspenseFallback';
import type { PublicationDynamicAttributes } from 'hb-builder/components/UIComponents/page/dynamic/Publication/types';
import { PAGE_VISIBILITY } from 'hb-builder/constants/pageVisibility';
import type { AuthenticationIds } from 'hb-builder/types/config/AuthenticationIds';
import { getFirstData } from 'hb-builder/utils/getFirstData';
import { useGetDynamicSearch } from 'hb-react/backoffice/requests/dynamicComponents/useGetDynamicSearch';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { APP_TYPE } from 'hb-react/types/appType';

export interface PublicationLayoutsProps {
  app: APP_TYPE;
  dynamicAttributes?: PublicationDynamicAttributes;
  ids?: AuthenticationIds;
  isPublic: boolean;
  navigate?: (value: string) => void;
  networkRootUrl?: string | null;
  type: 'topics' | 'events' | 'news';
  whiteLabelUrl?: string | null;
}

const EventsWrapper = lazy(
  () =>
    import(
      'hb-builder/components/UIComponents/page/dynamic/Publication/PublicationLayouts/EventsWrapper'
    ),
);

const NewsWrapper = lazy(
  () =>
    import(
      'hb-builder/components/UIComponents/page/dynamic/Publication/PublicationLayouts/NewsWrapper'
    ),
);
const TopicsWrapper = lazy(
  () =>
    import(
      'hb-builder/components/UIComponents/page/dynamic/Publication/PublicationLayouts/TopicsWrapper'
    ),
);

const defaultParams = {
  page: 1,
  per_page: 1,
  total_items: 1,
};

const PublicationLayouts = ({
  app,
  dynamicAttributes,
  ids,
  isPublic,
  navigate,
  networkRootUrl,
  type,
  whiteLabelUrl,
}: PublicationLayoutsProps) => {
  const { flagUjbDynamicComponentsPerf } = useFeatureFlag();
  const isBackOffice = app === APP_TYPE.BACKOFFICE;
  const pageVisibility = isPublic
    ? PAGE_VISIBILITY.public
    : PAGE_VISIBILITY.private;

  const commonQueryParams = {
    ...(dynamicAttributes?.query.dataType === 'manually'
      ? { item_id: dynamicAttributes?.itemId }
      : {
          categories: dynamicAttributes?.query.categories,
          type: dynamicAttributes?.query.dataType,
        }),
    ...defaultParams,
  };

  const params = {
    query: {
      ...(flagUjbDynamicComponentsPerf
        ? { component: 'publication' }
        : { page_visibility: pageVisibility }),
      ...commonQueryParams,
    },
  };

  const { data, error, isError, isLoading } = useGetDynamicSearch({
    app,
    params,
    type,
    whiteLabelUrl,
    enabled:
      dynamicAttributes?.query.dataType === 'manually'
        ? isNumber(dynamicAttributes.itemId)
        : undefined,
  });

  const isUnauthorized = error?.response?.status === 403;

  if (isUnauthorized) {
    return (
      <LoginCard
        componentName={type}
        iconName={type === 'news' ? 'file-lines' : 'calendar-day'}
      />
    );
  }

  if (isError) {
    return isBackOffice ? <ErrorState error={error} /> : null;
  }

  const firstDataItem = getFirstData(data);

  if (!firstDataItem && !isLoading) {
    return isBackOffice ? (
      <EmptyDynamicState component="publication" type={type} />
    ) : null;
  }

  const wrapperCommonProps = {
    data: firstDataItem,
    dynamicAttributes,
    ids,
    isBackOffice,
    isLoading,
    whiteLabelUrl,
  };

  return dynamicAttributes ? (
    <Suspense fallback={<SuspenseFallback />}>
      {type === 'events' && (
        <EventsWrapper {...wrapperCommonProps} navigate={navigate} />
      )}
      {type === 'news' && (
        <NewsWrapper {...wrapperCommonProps} navigate={navigate} />
      )}
      {type === 'topics' && (
        <TopicsWrapper
          {...wrapperCommonProps}
          networkRootUrl={networkRootUrl}
        />
      )}
    </Suspense>
  ) : null;
};

export default PublicationLayouts;
